import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/common/Pagination';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { useTableZoom } from '@/lib/hooks/useTableZoom';
import { useAuth } from '@/features/auth';
import { useSettingsFilters } from '../hooks';
import { contractPeriodsApi, settingsQueryKeys } from '../api';
import { SettingsDataTable, ContractPeriodAction, useSettingsColumns } from '../components';
import type { ContractPeriod } from '../types';

export function ContractPeriodsPage() {
  const { t } = useTranslation();
  const { hasAccess } = useAuth();
  const { filters, updateFilter, setPage, setLimit, handleSort } = useSettingsFilters();

  // Zoom is owned here so its control can sit in the Pagination row (left of
  // the per-page select) while the table applies the same value to its rows.
  const tableZoom = useTableZoom();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: settingsQueryKeys.contractPeriods.list(filters),
    queryFn: () => contractPeriodsApi.getAll(filters),
  });

  const columns = useSettingsColumns<ContractPeriod>({
    orderBy: filters.orderBy,
    order: filters.order,
    onSort: handleSort,
    resource: 'contract-period',
    apiEndpoint: 'Contract-Periods',
    queryKey: 'contractPeriods',
    title: t('contractPeriod'),
    showInterval: true,
  });

  return (
    <div>
      <div className="flex py-2 px-4 justify-between items-center border-b">
        <h1 className="text-xl font-semibold">{t('contractPeriod')}</h1>
        <div className="flex items-center gap-3">
          {hasAccess('contract', 'get') && (
            <Link to="/app/contracts">
              <Button variant="outline">{t('contracts')}</Button>
            </Link>
          )}
          {hasAccess('contract-period', 'create') && (
            <ContractPeriodAction />
          )}
        </div>
      </div>

      <div className="px-4 pt-4">
        <SettingsDataTable
          tableZoom={tableZoom}
          tableId="contract-periods"
          data={data?.results || []}
          columns={columns}
          filters={filters}
          updateFilter={updateFilter}
          isLoading={isLoading}
          isError={isError}
          error={error?.message}
          emptyMessage={t('noContractPeriods')}
          createButton={
            hasAccess('contract-period', 'create') ? (
              <ContractPeriodAction />
            ) : undefined
          }
        />

        <Pagination
          leadingControls={<TableZoomControls zoom={tableZoom} />}
          page={filters.page}
          limit={filters.limit}
          total={data?.meta?.total ?? 0}
          onPageChange={setPage}
          onLimitChange={setLimit}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
